import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Highcharts3D from "highcharts/highcharts-3d";
import Cylinder from "highcharts/modules/cylinder";
import GridLight from "highcharts/themes/grid-light";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import ChartFallbackImg from "./ChartFallbackImg";

Highcharts3D(Highcharts);
Cylinder(Highcharts);
GridLight(Highcharts);

const CylinderChart = ({ cylinderData }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const chartRef = useRef(null);

  const lowercaseRomanNumerals = (text) => {
    return text
      .replace(/\((?=[IVXLCDM]+\))/g, "(")
      .replace(/\b[IVXLCDM]+\b/g, (match) => match.toLowerCase());
  };

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) chart.reflow();
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const orderedSections = [
    "3(1)b(I)",
    "3(1)b(II)",
    "3(1)b(III)",
    "3(1)b(IV)",
    "3(1)b(V)",
    "3(1)b(VI)",
    "3(1)b(VII)",
    "3(1)b(VIII)",
    "3(1)b(IX)",
    "3(1)b(X)",
    "3(1)b(XI)",
  ];

  const displaySections = orderedSections.map(lowercaseRomanNumerals);

  const colors = [
    "#FF5733",
    "#8A2BE2",
    "#42E6A4",
    "#3357FF",
    "#FF33A1",
    "#FFD700",
    "#00CED1",
    "#FF4500",
    "#36BA98",
    "#836FFF",
    "#1E90FF",
  ];

  // Check: data exists and at least one value >0
  const hasData =
    cylinderData &&
    Object.keys(cylinderData).length > 0 &&
    Object.values(cylinderData).some((v) => v > 0);

  const options = {
    chart: {
      type: "cylinder",
      backgroundColor: "#ffffff",
      options3d: {
        enabled: true,
        alpha: 10,
        beta: 13,
        depth: 47,
        viewDistance: 80,
      },
      height: "618px",
    },
    title: {
      text: "Section Violations",
    },
    xAxis: {
      categories: displaySections,
      title: { text: null },
      labels: {
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          fontFamily: "Arial, sans-serif",
          color: "#333333",
        },
        rotation: -45,
      },
    },
    yAxis: { title: { text: null } },
    legend: { enabled: false },
    credits: { enabled: false },
    plotOptions: {
      series: {
        depth: 100,
        colorByPoint: true,
        point: {
          events: {
            click: (event) => {
              const clickedSection = event.point.original;
              setSelectedSection(clickedSection);
              setIsDrawerOpen(true);
            },
          },
        },
      },
    },
    series: [
      {
        name: "Count",
        data: orderedSections.map((key, index) => ({
          name: lowercaseRomanNumerals(key),
          original: key,
          y: cylinderData[key],
          color: colors[index % colors.length],
        })),
      },
    ],
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        maxHeight: "618px", // Match the parent container's max height
        overflow: "hidden", // Prevent overflow issues
      }}
      ref={chartRef}
    >
      {hasData ? (
        <>
          <HighchartsReact highcharts={Highcharts} options={options} />
          <SwipeableDrawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onOpen={() => setIsDrawerOpen(true)}
            PaperProps={{
              sx: {
                height: "100%",
                overflow: "hidden",
                borderTopLeftRadius: "12px",
                borderBottomLeftRadius: "12px",
              },
            }}
          >
            <Box
              sx={{
                width: 650,
                padding: 2,
                height: "100%",
                overflow: "hidden",
              }}
            >
              {selectedSection && <ChartDatadrawer section={selectedSection} />}
            </Box>
          </SwipeableDrawer>
        </>
      ) : (
        <ChartFallbackImg titleText="Section Violations" />
      )}
    </div>
  );
};

export default CylinderChart;
